import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { LoadingState, EmptyState, ErrorState } from "@/components/common/States";
import { RecipeEditor } from "@/components/common/RecipeEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ImportDialog } from "@/components/common/ImportDialog";
import { RECIPE_IMPORT } from "@/config/imports";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { toast } from "sonner";
import {
  Plus, Download, FileSpreadsheet, Search, Package, Loader2,
  Pencil, Trash2, ClipboardList,
} from "lucide-react";

const num = (v) => (v === null || v === undefined || v === "" ? null : Number(v));

export default function Recipes() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canManage = ["super_admin", "management", "qa_manager", "production_manager"].includes(role);
  const { data, isLoading, isError, error, refetch } = useResource("/recipes");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create");
  const [editRecipe, setEditRecipe] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [q, setQ] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const uniqueProducts = useMemo(() => new Set(rows.map((r) => r.productId || r.productName)).size, [rows]);
  const filtered = useMemo(() => {
    return rows
      .filter((r) => {
        if (!q) return true;
        const hay = `${r.recipeNumber} ${r.productName} ${r.productId} ${r.sku} ${r.category}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      })
      .sort((a, b) => String(a.productName).localeCompare(String(b.productName)) || Number(b.version || 0) - Number(a.version || 0));
  }, [rows, q]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["resource", "/recipes"] });

  const exportRecipes = async () => {
    setExporting(true);
    try {
      const res = await api.get("/recipes/export", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : "recipes-export.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Recipes exported");
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
    finally { setExporting(false); }
  };

  const handleSave = async (payload, action) => {
    setSubmitting(true);
    try {
      let recipeId;
      if (editorMode === "edit" && editRecipe?.id) {
        const res = await api.put(`/recipes/${editRecipe.id}`, payload);
        recipeId = res.data?.id || editRecipe.id;
      } else {
        const res = await api.post("/recipes", payload);
        recipeId = res.data?.id || res.data?.recipeId;
      }
      if (action === "submit" && recipeId) {
        await api.post(`/recipes/${recipeId}/submit`, undefined);
        toast.success("Recipe submitted for approval");
      } else {
        toast.success(editorMode === "edit" ? "Recipe updated" : "Recipe draft created");
      }
      setEditorOpen(false); setEditRecipe(null); setEditorMode("create");
      invalidate();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSubmitting(false); }
  };

  const openCreate = () => { setEditorMode("create"); setEditRecipe(null); setEditorOpen(true); };
  const openEdit = (r) => { setEditorMode("edit"); setEditRecipe(r); setEditorOpen(true); };

  const newVersion = async (r) => {
    setBusyId(r.id);
    try {
      const res = await api.post(`/recipes/${r.id}/new-version`);
      toast.success(`New draft version V${res.data?.version} created`);
      invalidate();
      if (res.data?.id) navigate(`/recipes/${res.data.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Could not create new version"));
    } finally { setBusyId(null); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/recipes/${toDelete.id}`);
      toast.success(`Recipe ${toDelete.recipeNumber} V${toDelete.version} deleted`);
      setToDelete(null);
      invalidate();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setDeleting(false); }
  };

  const isActive = (r) => ["active", "approved"].includes(String(r.status || "").toLowerCase());

  return (
    <div className="space-y-6" data-testid="recipe-master-page">
      <PageHeader title="Recipe Master" subtitle={`${uniqueProducts} products · ${rows.length} recipe versions · cost governance & approval`}>
        <Button data-testid="btn-export-recipes" variant="outline" className="gap-2" disabled={exporting} onClick={exportRecipes}>
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export
        </Button>
        <Button data-testid="btn-import-recipes" variant="outline" className="gap-2" onClick={() => setImportOpen(true)}>
          <FileSpreadsheet className="h-4 w-4" /> Import / Template
        </Button>
        <Button data-testid="btn-new-recipe" onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800">
          <Plus className="h-4 w-4" /> New Recipe
        </Button>
      </PageHeader>

      <div className="relative w-full sm:w-96">
        <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input data-testid="recipe-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search recipe / product / ID…" className="h-9 bg-white pl-8 text-sm" />
      </div>

      {isLoading ? <LoadingState label="Loading recipes…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        filtered.length === 0 ? (
          <EmptyState title="No recipes found" message="Create your first recipe — it starts as a Draft and can be submitted for approval."
            action={<Button data-testid="empty-new-recipe" onClick={openCreate} className="gap-2 bg-amber-700 hover:bg-amber-800"><Plus className="h-4 w-4" /> New Recipe</Button>} />
        ) : (
          <div className="space-y-4">
            {filtered.map((r) => {
              const active = isActive(r);
              const decided = num(r.decidedCost);
              const ingCost = num(r.currentIngredientCost ?? r.ingredientCost);
              const packCost = num(r.currentPackagingCost ?? r.packagingCost);
              return (
                <div key={r.id} data-testid={`recipe-card-${r.id}`}
                  className={`rounded-xl border bg-white shadow-sm ${active ? "border-amber-600/30 border-2" : "border-stone-200"}`}>
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-display text-base font-bold uppercase text-stone-900">{r.productName}</h3>
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-stone-500">
                          <Package className="h-3 w-3" />
                          <span className="font-mono">{r.productId}</span> · {r.productName}
                        </div>
                      </div>
                      <StatusBadge value={r.status} />
                    </div>

                    <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px]">
                      {r.category && <span className="rounded-full bg-stone-100 px-2.5 py-0.5 font-medium uppercase text-stone-600">{r.category}</span>}
                      <span className="rounded-full bg-stone-100 px-2.5 py-0.5 font-medium text-stone-600">{fmtNumber(r.yieldQty)} {r.yieldUnit}</span>
                      <span className="rounded-full bg-stone-900 px-2.5 py-0.5 font-mono font-semibold text-white">Recipe V{r.version}</span>
                      <span className="font-mono text-stone-400">{r.recipeNumber}</span>
                    </div>

                    {/* Metrics row — locked standard cost only; variance lives on production batches */}
                    {active ? (
                      <div className="mt-4 rounded-lg bg-stone-50/70 px-4 py-3">
                        <div data-testid={`recipe-card-decided-${r.id}`}>
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Decided / Standard Cost</div>
                          <div className="mt-0.5 font-mono text-sm font-bold text-stone-900">{fmtCurrency(decided)}</div>
                          <div className="mt-0.5 text-[10px] text-stone-400">Locked with the approved formulation — ingredient price changes do not move it</div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 grid grid-cols-3 gap-3 rounded-lg bg-stone-50/70 px-4 py-3">
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Ingredient</div>
                          <div className="mt-0.5 font-mono text-sm font-bold text-stone-900">{fmtCurrency(ingCost)}</div>
                        </div>
                        <div>
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Packaging</div>
                          <div className="mt-0.5 font-mono text-sm font-bold text-stone-900">{fmtCurrency(packCost)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">Cost / Unit</div>
                          <div className="mt-0.5 font-mono text-sm font-bold text-emerald-700">{fmtCurrency(r.currentUnitCost)}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action bar */}<div className={`grid ${active && canManage ? "grid-cols-3" : (!active && canManage ? "grid-cols-2" : "grid-cols-1")} divide-x divide-stone-100 border-t border-stone-100`}>
                    <button data-testid={`recipe-review-${r.id}`} onClick={() => navigate(`/recipes/${r.id}`)}
                      className="flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-700 hover:bg-stone-50">
                      <ClipboardList className="h-3.5 w-3.5" /> Review
                    </button>
                    {!active && canManage && (
                      <button data-testid={`recipe-edit-${r.id}`} onClick={() => openEdit(r)}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-amber-700 hover:bg-amber-50">
                        <Pencil className="h-3.5 w-3.5" /> Edit
                      </button>
                    )}
                    {active && canManage && (
                      <>
                        <button data-testid={`recipe-update-${r.id}`} onClick={() => newVersion(r)} disabled={busyId === r.id}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                          {busyId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5" />} Update
                        </button>
                        <button data-testid={`recipe-delete-${r.id}`} onClick={() => setToDelete(r)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-rose-600 hover:bg-rose-50">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      </>
                    )}
                  </div>

                </div>
              );
            })}
          </div>
        )}

      <RecipeEditor open={editorOpen} onOpenChange={(o) => { setEditorOpen(o); if (!o) { setEditRecipe(null); setEditorMode("create"); } }} mode={editorMode} initialValues={editRecipe} onSubmit={handleSave} submitting={submitting} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} config={RECIPE_IMPORT} onDone={invalidate} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this recipe version?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete && <>This permanently removes <strong>{toDelete.recipeNumber} V{toDelete.version}</strong> ({toDelete.productName}). The product will lose this recipe link.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recipe-delete-cancel">Keep</AlertDialogCancel>
            <AlertDialogAction data-testid="recipe-delete-confirm" className="bg-rose-600 hover:bg-rose-700" disabled={deleting} onClick={doDelete}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
